export type DayHours = { open: string; close: string; closed: boolean };

export type SellerHoursMap = Record<
  'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday' | 'Sunday',
  DayHours
>;

const DAYS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const;

export const DEFAULT_OPEN_TIME = '09:00';
export const DEFAULT_CLOSE_TIME = '22:00';

export function defaultDayHours(): DayHours {
  return { open: '', close: '', closed: true };
}

/** Times applied when a day is toggled open. */
export function openDayHours(): DayHours {
  return { open: DEFAULT_OPEN_TIME, close: DEFAULT_CLOSE_TIME, closed: false };
}

export function defaultSellerHours(): SellerHoursMap {
  return DAYS.reduce(
    (acc, day) => {
      acc[day] = defaultDayHours();
      return acc;
    },
    {} as SellerHoursMap,
  );
}

/** True when at least one weekday is marked open. */
export function sellerHoursHasOpenSchedule(hours: SellerHoursMap): boolean {
  return DAYS.some((day) => !hours[day].closed);
}

/** Fill missing open/close times on open days only. */
export function normalizeSellerHours(hours: SellerHoursMap): SellerHoursMap {
  const out = { ...hours } as SellerHoursMap;
  for (const day of DAYS) {
    const h = out[day];
    if (!h.closed) {
      out[day] = {
        open: h.open.trim() || DEFAULT_OPEN_TIME,
        close: h.close.trim() || DEFAULT_CLOSE_TIME,
        closed: false,
      };
    }
  }
  return out;
}

export function parseSellerHoursFromApi(
  hoursRaw?: string | Record<string, DayHours> | null,
  openFallback?: string | null,
  closeFallback?: string | null,
): SellerHoursMap {
  const base = defaultSellerHours();
  if (hoursRaw) {
    try {
      const parsed =
        typeof hoursRaw === 'string' ? (JSON.parse(hoursRaw) as Record<string, DayHours>) : hoursRaw;
      for (const day of DAYS) {
        const row = parsed[day];
        if (row) {
          base[day] = {
            open: row.open || '',
            close: row.close || '',
            closed: !!row.closed,
          };
        }
      }
      return normalizeSellerHours(base);
    } catch {
      /* fall through */
    }
  }
  if (openFallback || closeFallback) {
    for (const day of DAYS) {
      base[day] = {
        open: openFallback || DEFAULT_OPEN_TIME,
        close: closeFallback || DEFAULT_CLOSE_TIME,
        closed: false,
      };
    }
    return base;
  }
  return base;
}

export function sellerHoursHasAny(hours: SellerHoursMap): boolean {
  return DAYS.some((day) => {
    const h = hours[day];
    return !h.closed;
  });
}

function formatTimeShort(time: string): string {
  if (!time) return '';
  const [h, m] = time.split(':').map(Number);
  if (Number.isNaN(h)) return time;
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${String(m || 0).padStart(2, '0')} ${ampm}`;
}

/** One-line summary for collapsed hours picker */
export function sellerHoursSummary(hours: SellerHoursMap): string | null {
  if (!sellerHoursHasAny(hours)) return null;
  const openDays = DAYS.filter((d) => !hours[d].closed);
  if (openDays.length === 0) return 'Closed every day';
  const signatures = openDays.map((d) => `${hours[d].open}-${hours[d].close}`);
  const allSame =
    signatures.every((s) => s === signatures[0]) &&
    !!hours[openDays[0]!].open &&
    !!hours[openDays[0]!].close;
  if (allSame && openDays.length === 7) {
    const d = hours[openDays[0]!];
    return `Every day ${formatTimeShort(d.open)} – ${formatTimeShort(d.close)}`;
  }
  if (allSame && openDays.length >= 2) {
    const d = hours[openDays[0]!];
    return `${openDays.length} days · ${formatTimeShort(d.open)} – ${formatTimeShort(d.close)}`;
  }
  return `${openDays.length} day${openDays.length === 1 ? '' : 's'} open`;
}

/** Payload for API only when the seller set at least one open day. */
export function sellerHoursPayloadIfAny(
  hours: SellerHoursMap,
): { seller_hours: Record<string, DayHours> } | null {
  if (!sellerHoursHasAny(hours)) return null;
  return sellerHoursToPayload(hours);
}

export function sellerHoursToPayload(hours: SellerHoursMap): {
  seller_hours: Record<string, DayHours>;
} {
  const out: Record<string, DayHours> = {};
  for (const day of DAYS) {
    const h = hours[day];
    let open = h.open.trim();
    let close = h.close.trim();
    if (!h.closed) {
      if (!open) open = DEFAULT_OPEN_TIME;
      if (!close) close = DEFAULT_CLOSE_TIME;
    } else {
      open = '';
      close = '';
    }
    out[day] = { open, close, closed: h.closed };
  }
  return { seller_hours: out };
}

/** JSON string for store hours API (same Mon–Sun shape). */
export function sellerHoursToJson(hours: SellerHoursMap): string {
  return JSON.stringify(sellerHoursToPayload(hours).seller_hours);
}
